import { request } from './client'

/**
 * A school signing itself up.
 *
 * 🔴 THE FIRST CALL CREATES NOTHING, AND THAT IS THE WHOLE DESIGN.
 * `admin-panel.md` §3 says *"a school cannot mint its own first admin"*, which
 * is why the operator's provisioning route is `SUPER_ADMIN`-only. `requestSchoolSignup`
 * does not reverse it: the server writes one pending row and issues a single-use
 * link, and the school, its admin account and its subscription are written by
 * `claimSchoolSignup` — which needs that link back.
 *
 * So the worst a script can do here is fill a table with rows that expire in 72
 * hours, rather than mint working staff logins into schools nobody agreed to
 * serve.
 */

export interface SchoolSignupBody {
  schoolName: string
  adminName: string
  adminEmail: string
  /** `Plan.code` from the public price list — never an id. */
  planCode: string
  /** The ceiling this school is asking for. Becomes `Subscription.seatLimit`. */
  seats: number
}

export interface SignupRequested {
  sent: true
  /**
   * 🔴 ALWAYS `null` IN A REAL DEPLOYMENT, AND THE SCREEN MUST NOT DEPEND ON IT.
   *
   * This repo has no email sender — `invite.prisma` records the gap in as many
   * words — so the link is returned here only under `SIGNUP_DEV_ECHO`, a flag
   * the server refuses to boot with in production. A screen that showed the link
   * whenever it was present is correct; one that *required* it would break the
   * day the mailer lands.
   */
  token: string | null
  expiresAt: string
}

/**
 * `POST /auth/school/signup`.
 *
 * ⚠️ IT ANSWERS THE SAME WAY WHETHER OR NOT THE ADDRESS IS TAKEN — the rule the
 * parent OTP request already lives by. So the screen must not branch on the
 * response to say "that email is registered": it does not know, deliberately.
 */
export const requestSchoolSignup = (
  body: SchoolSignupBody,
  signal?: AbortSignal,
): Promise<SignupRequested> =>
  request<SignupRequested>('/auth/school/signup', {
    method: 'POST',
    // Public: this is the call made by somebody who has no account yet.
    context: 'public',
    body,
    ...(signal ? { signal } : {}),
  })

export interface ClaimedSignup {
  schoolId: string
  schoolName: string
  email: string
}

/**
 * `POST /auth/school/signup/claim` — the link comes back, the school is created.
 *
 * ⚠️ ONE GENERIC 401 FOR UNKNOWN, EXPIRED AND ALREADY-CLAIMED ALIKE, so the
 * screen has one sentence for all three. A 409 is different and worth showing
 * verbatim: it means the name or the address was taken in the meantime, which is
 * something the person reading can act on.
 */
export const claimSchoolSignup = (
  token: string,
  password: string,
  signal?: AbortSignal,
): Promise<ClaimedSignup> =>
  request<ClaimedSignup>('/auth/school/signup/claim', {
    method: 'POST',
    context: 'public',
    body: { token, password },
    ...(signal ? { signal } : {}),
  })
